import React, { useState, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  TrendingUp, TrendingDown, Activity, Target, CheckCircle2, XCircle,
  AlertCircle, AlertTriangle, RefreshCw, Loader2, Database, Clock,
  Play, ChevronRight, BookOpen, ShieldCheck,
} from 'lucide-react';
import apiClient from '../../lib/apiClient';
import logger from '../../lib/logger';
import './ClassifierHealth.css';

/**
 * ClassifierHealth — accuracy-trend dashboard for the classification engine.
 *
 * This is the user-facing surface for the nightly `classification_eval_runs`
 * cron. Per CLASSIFICATION_NORTH_STAR.md, the Continuous Learning Loop's
 * job is to walk the accuracy floor up over time — this page is where you
 * see whether it actually does.
 *
 * Surfaces the existing /api/classification/eval/* endpoints (stats / runs /
 * runs/:id / runs/trigger). No new backend work is required — the
 * infrastructure has been in place since Phase H.11 baseline; Phase 4 of
 * the flag-and-fix feature promotes it to a first-class nav entry so it's
 * actually visited.
 */

function fmtPct(v) {
  if (v == null) return '—';
  return `${(Number(v) * 100).toFixed(1)}%`;
}

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function fmtDuration(ms) {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60_000).toFixed(1)}m`;
}

// Tiny inline SVG sparkline-bar chart. No external lib; theme-following
// via fill="currentColor" + CSS color rules. Grid-stripes are subtle
// surface tones so light + dark both look fine.
function AccuracyTrendChart({ runs }) {
  if (!runs || runs.length === 0) {
    return <div className="ch-trend__empty">No runs in the last 30 days.</div>;
  }
  const values = runs.map(r => Number(r.accuracy) || 0);
  const maxY = Math.max(1, ...values);
  // Render up to last 30 bars
  const points = runs.slice(-30);
  const w = 100; // viewBox width (%)
  const h = 60;  // viewBox height (units)
  const barW = w / Math.max(points.length, 1);
  // Floor lines: 0.80, 0.90, 0.95, 0.99 — the North Star walk targets
  const floors = [
    { y: 0.80, label: '80%' },
    { y: 0.90, label: '90% — current floor' },
    { y: 0.95, label: '95% — target' },
    { y: 0.99, label: '99% — stretch' },
  ];
  return (
    <div className="ch-trend">
      <svg className="ch-trend__svg" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-label="Accuracy trend">
        {floors.map(f => {
          const y = h - (f.y / maxY) * h;
          return (
            <g key={f.y}>
              <line x1="0" x2={w} y1={y} y2={y} className="ch-trend__floor" />
            </g>
          );
        })}
        {points.map((r, i) => {
          const value = Number(r.accuracy) || 0;
          const barH = (value / maxY) * h;
          const x = i * barW;
          const y = h - barH;
          // Color by floor band
          const band =
            value >= 0.95 ? 'high' :
            value >= 0.90 ? 'good' :
            value >= 0.80 ? 'warn' : 'low';
          return (
            <g key={r.run_id || i}>
              <rect
                className={`ch-trend__bar ch-trend__bar--${band}`}
                x={x + barW * 0.1}
                y={y}
                width={barW * 0.8}
                height={barH}
              >
                <title>
                  Run #{r.run_id}{'\n'}
                  {fmtDate(r.finished_at)}{'\n'}
                  Accuracy: {fmtPct(r.accuracy)} ({r.total_items} items)
                </title>
              </rect>
            </g>
          );
        })}
      </svg>
      <div className="ch-trend__legend">
        {floors.map(f => (
          <span key={f.y} className="ch-trend__legend-item">
            <span className="ch-trend__legend-dash" /> {f.label}
          </span>
        ))}
      </div>
    </div>
  );
}

function StatTile({ icon: Icon, label, value, sub, tone, alert, loading }) {
  const cls = ['ch-stat', tone ? `ch-stat--${tone}` : '', alert ? 'ch-stat--alert' : ''].filter(Boolean).join(' ');
  // Spinner in place of the value while the underlying query hasn't
  // returned — keeps the tile structure stable but signals "syncing"
  // instead of showing a misleading placeholder.
  const showSpinner = loading && (value == null || value === '—');
  return (
    <div className={cls}>
      <Icon size={16} className="ch-stat__icon" />
      <div className="ch-stat__body">
        <div className="ch-stat__value">
          {showSpinner ? <Loader2 size={20} className="ch-spin" /> : value}
        </div>
        <div className="ch-stat__label">{label}</div>
        {sub && <div className="ch-stat__sub">{sub}</div>}
      </div>
    </div>
  );
}

function RunHistoryRow({ run, onSelect }) {
  const acc = Number(run.accuracy);
  const band =
    acc >= 0.95 ? 'high' :
    acc >= 0.90 ? 'good' :
    acc >= 0.80 ? 'warn' : 'low';
  return (
    <tr className="ch-history__row" onClick={() => onSelect(run.run_id)}>
      <td className="ch-mono">#{run.run_id}</td>
      <td>{fmtDate(run.started_at)}</td>
      <td>
        <span className={`ch-acc ch-acc--${band}`}>{fmtPct(run.accuracy)}</span>
      </td>
      <td className="ch-num">{run.correct_count}/{run.total_items}</td>
      <td className="ch-num">{run.errored_count || 0}</td>
      <td>{fmtDuration(run.duration_ms)}</td>
      <td className="ch-mono ch-muted">{run.prompt_version || '—'}</td>
      <td className={`ch-status ch-status--${run.status}`}>{run.status}</td>
      <td className="ch-num"><ChevronRight size={12} /></td>
    </tr>
  );
}

function RunDetailDrawer({ runId, onClose }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['classifier-health', 'run', runId],
    queryFn: () => apiClient.get(`/api/classification/eval/runs/${runId}`),
    enabled: !!runId,
  });

  if (!runId) return null;

  const run = data?.run;
  const items = data?.items || [];
  const incorrect = items.filter(i => !i.correct);

  return (
    <aside className="ch-drawer" role="dialog" aria-modal="true" aria-label="Run detail">
      <div className="ch-drawer__head">
        <h2>Run #{runId}</h2>
        <button className="ch-icon-btn" onClick={onClose}><XCircle size={16} /></button>
      </div>
      {isLoading && <div className="ch-state"><Loader2 className="ch-spin" size={16} /> Loading…</div>}
      {error && <div className="ch-state ch-state--error"><AlertCircle size={16} /> {error.message}</div>}
      {run && (
        <>
          <section className="ch-drawer__summary">
            <div><span className="ch-meta-label">Accuracy</span><strong>{fmtPct(run.accuracy)}</strong></div>
            <div><span className="ch-meta-label">Lenient</span>{fmtPct(run.accuracy_lenient)}</div>
            <div><span className="ch-meta-label">Items</span>{run.total_items}</div>
            <div><span className="ch-meta-label">Duration</span>{fmtDuration(run.duration_ms)}</div>
            <div><span className="ch-meta-label">Prompt</span><span className="ch-mono">{run.prompt_version || '—'}</span></div>
          </section>
          {run.error_message && (
            <div className="ch-state ch-state--error">
              <AlertCircle size={14} /> {run.error_message}
            </div>
          )}
          <section className="ch-drawer__items">
            <h3>Incorrect predictions ({incorrect.length} of {items.length})</h3>
            {incorrect.length === 0 ? (
              <p className="ch-muted">Every item correct — no diffs to show.</p>
            ) : (
              <ul className="ch-incorrect">
                {incorrect.slice(0, 100).map(it => (
                  <li key={it.id} className="ch-incorrect__item">
                    <p className="ch-incorrect__desc" title={it.item_description}>
                      {it.item_description?.slice(0, 180) || <em>(no description)</em>}
                      {it.item_description?.length > 180 ? '…' : ''}
                    </p>
                    <div className="ch-incorrect__diff">
                      <span><span className="ch-meta-label">Truth</span>{it.truth_component_name || '—'}</span>
                      <span className="ch-incorrect__sep">→</span>
                      <span className="ch-incorrect__predicted">
                        <span className="ch-meta-label">Predicted</span>
                        {it.predicted_component_name || <em className="ch-muted">no prediction</em>}
                      </span>
                      <span className="ch-mono ch-muted">{fmtPct(it.confidence)}</span>
                    </div>
                    {it.reasoning && (
                      <p className="ch-incorrect__reasoning">{it.reasoning.slice(0, 240)}</p>
                    )}
                  </li>
                ))}
                {incorrect.length > 100 && (
                  <li className="ch-muted">{incorrect.length - 100} more not shown — investigate via SQL.</li>
                )}
              </ul>
            )}
          </section>
        </>
      )}
    </aside>
  );
}

function ClassifierHealth() {
  const queryClient = useQueryClient();
  const [selectedRun, setSelectedRun] = useState(null);

  const statsQuery = useQuery({
    queryKey: ['classifier-health', 'stats'],
    queryFn: () => apiClient.get('/api/classification/eval/stats'),
    staleTime: 60_000,
  });

  const runsQuery = useQuery({
    queryKey: ['classifier-health', 'runs'],
    queryFn: () => apiClient.get('/api/classification/eval/runs', { params: { limit: 30 } }),
    staleTime: 60_000,
  });

  const triggerMutation = useMutation({
    mutationFn: () => apiClient.post('/api/classification/eval/runs/trigger', {}),
    onSuccess: () => {
      logger.ui.info('ClassifierHealth', 'run_triggered');
      // Eval runs take time — refresh stats/runs after 5s to pick up the
      // 'running' row, then keep refetching periodically until it lands.
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['classifier-health'] });
      }, 5000);
    },
    onError: (err) => logger.ui.error('ClassifierHealth', 'trigger_failed', { message: err?.message }),
  });

  const stats = statsQuery.data;
  const runs = runsQuery.data?.runs || [];
  const latest = stats?.latest_run;
  const trend = stats?.trend_30d || [];

  // Drift delta = (latest accuracy) - (mean of prior 7d, excluding latest)
  const driftDelta = useMemo(() => {
    if (!latest || trend.length < 2) return null;
    const prior = trend.slice(0, -1).slice(-7);
    if (prior.length === 0) return null;
    const mean = prior.reduce((s, r) => s + Number(r.accuracy), 0) / prior.length;
    return Number(latest.accuracy) - mean;
  }, [latest, trend]);

  const latestAccuracy = latest ? Number(latest.accuracy) : null;
  const accBand = latestAccuracy == null ? 'unknown' :
    latestAccuracy >= 0.95 ? 'high' :
    latestAccuracy >= 0.90 ? 'good' :
    latestAccuracy >= 0.80 ? 'warn' : 'low';

  return (
    <div className="ch-page">
      <header className="ch-page__head">
        <div className="ch-page__title">
          <Activity size={22} />
          <div>
            <h1>Classifier health</h1>
            <p className="ch-page__sub">
              Nightly cron measures the engine against{' '}
              <code>classification_eval_set</code> (human-labeled ground truth).
              Per CLASSIFICATION_NORTH_STAR.md, the Continuous Learning Loop
              walks the floor from 18/20 (today) toward 19.8/20 (target). This
              page is where you watch that happen — or notice when it doesn't.
            </p>
          </div>
        </div>
        <div className="ch-page__principles">
          <span className="ch-pill"><BookOpen size={11} /> Layer 1 · drift detection</span>
          <span className="ch-pill"><ShieldCheck size={11} /> Walks the accuracy floor up</span>
        </div>
      </header>

      {stats?.active_drop_alert && (
        <div className="ch-alert">
          <AlertTriangle size={16} />
          <div>
            <strong>Accuracy drop alert is firing.</strong> The latest run
            crossed a notifier threshold compared to recent history. Investigate
            the most-recent run below and check{' '}
            <code>scripts/snapshot-classification-quality.js</code> output if
            the drop persists.
          </div>
        </div>
      )}

      <section className="ch-stats">
        <StatTile
          icon={latestAccuracy != null && latestAccuracy >= 0.9 ? Target : AlertCircle}
          label="Latest accuracy"
          value={stats && latestAccuracy != null ? fmtPct(latestAccuracy) : (stats ? '—' : null)}
          sub={stats ? (latest ? `Run #${latest.run_id} · ${fmtDate(latest.finished_at)}` : 'No run yet') : null}
          tone={accBand}
          alert={accBand === 'low'}
          loading={statsQuery.isLoading}
        />
        <StatTile
          icon={driftDelta == null ? Activity : driftDelta >= 0 ? TrendingUp : TrendingDown}
          label="Drift · 7-day"
          value={stats ? (driftDelta == null ? '—' : `${driftDelta >= 0 ? '+' : ''}${(driftDelta * 100).toFixed(2)}pp`) : null}
          sub="vs. prior 7-day mean"
          tone={driftDelta == null ? null : driftDelta >= 0 ? 'high' : 'warn'}
          loading={statsQuery.isLoading}
        />
        <StatTile
          icon={Database}
          label="Eval set size"
          value={stats ? (stats.eval_set_size ?? 0).toLocaleString() : null}
          sub={stats ? `${stats.labeled_30d ?? 0} added in last 30d · ${stats.labeler_count ?? 0} labelers` : null}
          tone="primary"
          loading={statsQuery.isLoading}
        />
        <StatTile
          icon={Clock}
          label="Last run"
          value={stats ? (latest ? fmtDuration(latest.duration_ms) : '—') : null}
          sub={stats ? (latest ? `${latest.correct_count}/${latest.total_items} correct` : 'No run yet') : null}
          loading={statsQuery.isLoading}
        />
      </section>

      <section className="ch-section">
        <div className="ch-section__head">
          <h2>Accuracy trend · last 30 days</h2>
          <button
            className="ch-action"
            onClick={() => triggerMutation.mutate()}
            disabled={triggerMutation.isPending}
            title="Fire an eval run now (admin only). Runs against the full active eval set; results land in the table below in ~30s-2min."
          >
            {triggerMutation.isPending ? <Loader2 size={14} className="ch-spin" /> : <Play size={14} />}
            Run eval now
          </button>
        </div>
        {statsQuery.isLoading ? (
          <div className="ch-state"><Loader2 className="ch-spin" size={16} /> Loading trend…</div>
        ) : (
          <AccuracyTrendChart runs={trend} />
        )}
      </section>

      <section className="ch-section">
        <div className="ch-section__head">
          <h2>Recent runs</h2>
          <button
            className="ch-icon-btn ch-icon-btn--ghost"
            onClick={() => { statsQuery.refetch(); runsQuery.refetch(); }}
            disabled={runsQuery.isFetching}
          >
            <RefreshCw size={14} className={runsQuery.isFetching ? 'ch-spin' : ''} />
          </button>
        </div>
        {runsQuery.isLoading ? (
          <div className="ch-state"><Loader2 className="ch-spin" size={16} /> Loading runs…</div>
        ) : runsQuery.error ? (
          <div className="ch-state ch-state--error"><AlertCircle size={16} /> {runsQuery.error.message}</div>
        ) : runs.length === 0 ? (
          <div className="ch-state ch-state--empty">
            <CheckCircle2 size={28} />
            <h3>No runs yet</h3>
            <p>The nightly cron runs at 02:30 PT. You can also trigger one above.</p>
          </div>
        ) : (
          <div className="ch-history-wrap">
            <table className="ch-history">
              <thead>
                <tr>
                  <th>Run</th>
                  <th>Started</th>
                  <th>Accuracy</th>
                  <th>Correct</th>
                  <th>Errored</th>
                  <th>Duration</th>
                  <th>Prompt</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {runs.map(r => (
                  <RunHistoryRow
                    key={r.run_id}
                    run={r}
                    onSelect={(id) => setSelectedRun(id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {selectedRun && (
        <RunDetailDrawer
          runId={selectedRun}
          onClose={() => setSelectedRun(null)}
        />
      )}
    </div>
  );
}

export default ClassifierHealth;
